import { AttributeDefinedValueReadModel, AttributeEditorReadModel, CalcScriptResultReadModel, EmailAttributeDefinedValuesFromPeopleTagReadModel } from "@Alternatives/Attributes/Domain/readmodels";
import { DefinedValue, RealDefinedValue } from "@Alternatives/Model";
import { generateName, strCompareLowerCase } from "@common/Common";
import { ValidationError } from "@common/types";


export class AttributeEditorDialogViewModel
{
    opened: boolean = false;
    initialized: boolean = false;

    dialogTitle: string = '';
    confirmButtonLabel: string = '';
    scriptTestResult: string | null = null;
    scriptTestInputs: { Name: string, Value: number }[] = [];
    scales: { Id: number, Name: string, Levels: { Name: string, Value: number, Description: string }[] }[] = [];
    peopleTags: string[] = [];
    numericAttributeNames: string[] = [];

    errors: ValidationError[] = [];

    allowChangeAttributeType: boolean = true;
    allowMultipleValuesEnabled: boolean = true;
    allowInputTypeEvaluate: boolean = true;

    attributeId: number = 0;
    sourceId: number | null = null;
    internalName: string | null = null;
    name: string | null = null;
    section: string | null = null;
    description: string | null = null;
    script: string | null = null;
    attributeType: string = "Real";
    inputType: string = "Manual";
    isMultivalue: boolean = false;
    minLength: number | null = null;
    maxLength: number | null = null;
    minValue: number | null = null;
    maxValue: number | null = null;
    minDate: Date | null = null;
    maxDate: Date | null = null;
    rows: number | null = null;
    definedValues: AttributeDefinedValueReadModel[] = [];

    showCreator(initData: AttributeCreatorInitData)
    {
        this.attributeId = 0;
        this.sourceId = null;

        this.allowChangeAttributeType = initData.allowChangeAttributeType;
        this.allowMultipleValuesEnabled = initData.allowMultipleValuesEnabled;
        this.allowInputTypeEvaluate = initData.allowInputTypeEvaluate;

        this.internalName = initData.defaultInternalName;
        this.name = initData.defaultName;
        this.section = initData.defaultSection;
        this.description = initData.defaultDescription;
        this.attributeType = initData.defaultType ?? "Real";
        this.inputType = initData.defaultInputType ?? "Manual";        
        this.minValue = initData.defaultMinValue;
        this.definedValues = initData.defaultRealDefinedValues ?? [];

        this.dialogTitle = "Create attribute";
        this.confirmButtonLabel = "Create";

        this.clearValidationErrors();

        this.opened = true;
    }

    showEditor(attrId: number, attrName: string)
    {
        this.attributeId = attrId;
        this.sourceId = null;
        
        this.name = attrName;        

        this.dialogTitle = `Edit attribute: ${attrName}`;
        this.confirmButtonLabel = "Update";
        this.clearValidationErrors();

        this.allowChangeAttributeType = false;

        this.opened = true;
    }

    showCloner(sourceId: number, sourceName: string)
    {
        this.attributeId = 0;
        this.sourceId = sourceId;
        this.name = generateName(sourceName);

        this.dialogTitle = "Create attribute";
        this.confirmButtonLabel = "Create";

        this.clearValidationErrors();

        this.opened = true;
    }

    setData(rm: AttributeEditorReadModel)
    {
        this.minLength = null;
        this.maxLength = null;
        this.maxValue = null;
        this.minDate = null;
        this.maxDate = null;
        this.isMultivalue = false;
        this.script = null;
        this.rows = null;

        this.scriptTestResult = null;
        this.scriptTestInputs = [];

        this.scales = rm.Scales;
        this.peopleTags = rm.PeopleTags;
        this.numericAttributeNames = rm.NumericAttributeNames;

        //Update or Clone
        if (this.attributeId || this.sourceId)
        {
            this.internalName = null;

            this.section = rm.Section;
            this.description = rm.Description;
            this.script = rm.Script;
            this.attributeType = rm.Type;
            this.inputType = rm.InputType;
            this.isMultivalue = rm.IsMultivalue;
            this.minLength = rm.MinLength;
            this.maxLength = rm.MaxLength;
            this.minValue = rm.MinValue;
            this.maxValue = rm.MaxValue;
            this.minDate = rm.MinDate ? new Date(rm.MinDate) : null;
            this.maxDate = rm.MaxDate ? new Date(rm.MaxDate) : null;
            this.rows = rm.Rows;
            this.definedValues = rm.DefinedValues ?? [];
        }

        this.initialized = true;
    }

    setScriptTestResult(rm: CalcScriptResultReadModel)
    {
        this.scriptTestResult = rm.Error ?? rm.Result?.toString();
    }

    setPeople(rm: EmailAttributeDefinedValuesFromPeopleTagReadModel)
    {
        const existing = this.definedValues
            .filter(a => a.Value != null)
            .map(a => String(a.Value).toLowerCase());

        for (const dv of rm.DefinedValues)
        {
            const email = String(dv.Value);
            if (!existing.includes(email))
            {
                this.definedValues.push(dv);
            }
        }

        this.definedValues = this.definedValues.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
    }

    clearValidationErrors()
    {
        this.errors = [];
    }

    displayValidationErrors(errors: ValidationError[])
    {
        this.errors = errors;
    }

    close()
    {
        this.opened = false;
        this.clearValidationErrors();
    }
}


class AttributeCreatorInitData
{
    allowChangeAttributeType: boolean = true;
    allowMultipleValuesEnabled: boolean = true;
    allowInputTypeEvaluate: boolean = true;

    defaultName: string = '';

    defaultInternalName?: string | null = null;
    defaultSection?: string | null = null;
    defaultDescription?: string | null = null;
    defaultType?: string | null = null;
    defaultInputType?: string | null = null;
    defaultMinValue?: number | null = null;
    defaultRealDefinedValues?: RealDefinedValue[] | null = null;
}

export class TestScriptEventPayload
{
    Script: string;
    ScriptTestInputs: { Name: string, Value: number }[];
    constructor(script: string, scriptTestInputs: { Name: string, Value: number }[])
    {
        this.Script = script;
        this.ScriptTestInputs = scriptTestInputs;
    }
}


export class SaveNumericAttributeEventPayload
{
    Id: number;

    Name: string;
    Description: string;
    Section: string;
    InputType: string;
    AllowMultipleValues: boolean;

    MinValue: number | null;
    MaxValue: number | null;
    Script: string | null;
    DefinedValues: RealDefinedValue[] | null;

    constructor(id: number, name: string, description: string, section: string, inputType: string, allowMultipleValues: boolean,
        minValue: number | null, maxValue: number | null, script: string | null, definedValues: RealDefinedValue[] | null)
    {
        this.Id = id;

        this.Name = name;
        this.Description = description;
        this.Section = section;
        this.InputType = inputType;
        this.AllowMultipleValues = allowMultipleValues;

        this.MinValue = minValue;
        this.MaxValue = maxValue;
        this.Script = script;
        this.DefinedValues = definedValues;
    }
}

export class SaveDateAttributeEventPayload
{
    Id: number;

    Name: string;
    Description: string;
    Section: string;
    InputType: string;
    AllowMultipleValues: boolean;

    MinValue: Date | null;
    MaxValue: Date | null;
    DefinedValues: DefinedValue[] | null;

    constructor(attributeId: number, name: string, description: string, section: string, inputType: string, allowMultipleValues: boolean,
        minValue: Date | null, maxValue: Date | null, definedValues: DefinedValue[] | null)
    {
        this.Id = attributeId;

        this.Name = name;
        this.Description = description;
        this.Section = section;
        this.InputType = inputType;
        this.AllowMultipleValues = allowMultipleValues;

        this.MinValue = minValue;
        this.MaxValue = maxValue;
        this.DefinedValues = definedValues;
    }
}

export class SaveTextAttributeEventPayload
{
    Id: number;

    Name: string;
    Description: string;
    Section: string;
    InputType: string;
    AllowMultipleValues: boolean;

    MinLength: number | null;
    MaxLength: number | null;
    DefinedValues: DefinedValue[] | null;

    constructor(attributeId: number, name: string, description: string, section: string, inputType: string, allowMultipleValues: boolean,
        minLength: number | null, maxLength: number | null, definedValues: DefinedValue[] | null)
    {
        this.Id = attributeId;

        this.Name = name;
        this.Description = description;
        this.Section = section;
        this.InputType = inputType;
        this.AllowMultipleValues = allowMultipleValues;

        this.MinLength = minLength;
        this.MaxLength = maxLength;
        this.DefinedValues = definedValues;
    }
}

export class SaveEmailAttributeEventPayload
{
    Id: number;

    Name: string;
    Description: string;
    Section: string;
    InputType: string;
    AllowMultipleValues: boolean;

    DefinedValues: DefinedValue[] | null;

    constructor(attributeId: number, name: string, description: string, section: string, inputType: string,
        allowMultipleValues: boolean, definedValues: DefinedValue[] | null)
    {
        this.Id = attributeId;

        this.Name = name;
        this.Description = description;
        this.Section = section;
        this.InputType = inputType;
        this.AllowMultipleValues = allowMultipleValues;

        this.DefinedValues = definedValues;
    }
}

export class SaveMultilineAttributeEventPayload
{
    Id: number;

    Name: string;
    Description: string;
    Section: string;

    Rows: number;
    MinLength: number | null;
    MaxLength: number | null;

    constructor(attributeId: number, name: string, description: string, section: string, rows: number,
        minLength: number | null, maxLength: number | null)
    {
        this.Id = attributeId;

        this.Name = name;
        this.Description = description;
        this.Section = section;

        this.Rows = rows;
        this.MinLength = minLength;
        this.MaxLength = maxLength;
    }
}


